import { useTranslations } from 'use-intl'

import { cn } from '@/lib/utils'
import { BookDemoButton } from '@/components/cta-buttons'
import { HighlightText } from '@/components/ui/highlight-text'
import { Reveal } from '@/components/ui/reveal'
import { useSectionView } from '@/lib/analytics/use-section-view'

/**
 * Height is dictated by the content — no `--landing-band-h` here, this card is
 * not a full-bleed band. No default top padding either: on the landing the FAQ
 * above provides it — pages where the previous section is flush (a band) pass
 * their own `pt-*` via `className`.
 *
 * `trackSectionView`: only the home page wants this decisive section tracked
 * (spec: sections clés seulement) even though this component renders on
 * several pages, so tracking is opt-in per caller.
 *
 * `onCtaClick`: page-specific click tracking (e.g. About's `about_cta_click`)
 * composes with the button's own navigation handler — also opt-in per caller.
 */
export function FinalCtaSection({
  className,
  trackSectionView = false,
  onCtaClick,
}: {
  className?: string
  trackSectionView?: boolean
  onCtaClick?: () => void
}) {
  const t = useTranslations('home.finalCta')
  const sectionViewRef = useSectionView('home', 'final_cta')

  return (
    <section
      ref={trackSectionView ? sectionViewRef : undefined}
      className={cn('pb-12 md:pb-20 lg:pb-28', className)}
    >
      {/* The Reveal sits outside the card, which keeps its `isolate`: the
          `-z-10` layers (grid, icons) stay trapped inside it. No inner cascade —
          the mascot is half the card, having it arrive after its own heading
          would look odd. */}
      <Reveal className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* `isolate` keeps the `-z-10` layers inside the card;
            `overflow-hidden` clips the icons and the mascot against the rounded
            corners. No `pb-*`: that is what sticks the mascot to the edge. */}
        <div className="relative isolate overflow-hidden rounded-[2.5rem] bg-linear-to-r/srgb from-[#0211D9] to-[#2736F8]">
          {/* `inset-0` makes the layer exactly card-sized, which is what
              `bg-grid-lines`' calc() divides into 8×5. Rules are translucent
              white: `--border` would vanish against this blue. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-10 bg-grid-lines [--grid-line-color:rgba(255,255,255,0.08)] [--grid-rows:5]"
          />

          {/* After the grid in the DOM, so painted over it at equal `-z-10`.
              The image is a full-width layer (icons left and right, empty
              centre) at a 2.01 ratio — `object-cover` crops from the sides on
              mobile, where the side icons fall out of frame. */}
          <img
            src="/apps/icons/floating-icons-blue.webp"
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
            width={1566}
            height={779}
            className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover motion-safe:animate-float"
          />

          <div className="px-6 pt-8 text-center lg:pt-10">
            <h2 className="font-serif text-3xl leading-tight font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">
              <span className="block">
                {t('titleLead')}{' '}
                <HighlightText className="bg-app-highlight-light">
                  {t('titleHighlight')}
                </HighlightText>
              </span>
              <span className="mt-1 block">{t('titleTail')}</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-white/80 sm:text-lg">
              {t('description')}
            </p>
            {/* Halo: two white shadows, not a `ring` — the halo must spill off
                the button onto the blue, not outline it. The `spread` (2nd
                length) thickens the core, the `blur` spreads it. Do not push the
                2nd past ~70px of blur: the halo then reaches the mascot's head
                and washes the card out. */}
            <BookDemoButton
              source="final_cta"
              variant="background"
              size="lg"
              onClick={onCtaClick}
              className="mt-8 h-12 px-8 text-sm sm:h-11 lg:text-base shadow-[0_0_24px_4px_rgba(255,255,255,0.6),0_0_64px_12px_rgba(255,255,255,0.25)]"
            >
              {t('cta')}
            </BookDemoButton>
          </div>

          {/* Last child in flow, card with no bottom padding → the mascot meets
              the bottom edge at every width, without absolute positioning. */}
          <img
            src="/mascot/mascot-cross-arm.webp"
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
            width={642}
            height={468}
            className="pointer-events-none mx-auto mt-6 block w-64 max-w-full sm:w-80 lg:w-96"
          />
        </div>
      </Reveal>
    </section>
  )
}
